import type { ReputationInsightDto } from '@up2stay/up2promo-api-client';
import {
  Badge,
  Button,
  KpiCard,
  SegmentedControl,
  Skeleton,
} from '@up2stay/up2promo-ui';
import { Plug, Reply, Star } from 'lucide-react';
import { useMemo, useState } from 'react';

import { AppIonPage } from '@/core/components/app-ion-page';
import { AppPageContent } from '@/core/components/app-page-content';
import { AppPageLayout } from '@/core/components/app-page-layout';
import { ListEmptyState } from '@/core/components/layout';
import { PageHeader } from '@/core/components/page-header';
import { AiSummaryCard } from '@/core/components/reputation/ai-summary-card';
import { LocationCombobox } from '@/core/components/reputation/location-combobox';
import { OverviewAttentionCard } from '@/core/components/reputation/overview/overview-attention-card';
import { OverviewCompetitorCard } from '@/core/components/reputation/overview/overview-competitor-card';
import { OverviewReviewsCard } from '@/core/components/reputation/overview/overview-reviews-card';
import {
  ReputationLayout,
  ReputationPageBanners,
} from '@/core/components/reputation/reputation-layout';
import {
  REPUTATION_RANGE_DAYS,
  type ReputationRangeDays,
  reputationWindowFrom,
} from '@/core/components/reputation/reputation-range';
import {
  formatReputationScoreValue,
  reputationScoreFooter,
  ReputationScoreFormulaPopover,
  type ScoreFormulaLabels,
} from '@/core/components/reputation/reputation-score-formula';
import {
  useReputationScopeLocation,
  useReputationSync,
} from '@/core/components/reputation/reputation-sync-context';
import { ReputationSyncPlaceholder } from '@/core/components/reputation/reputation-sync-placeholder';
import { downsample } from '@/core/components/reputation/downsample';
import { useEntitlements } from '@/core/hooks/use-entitlements';
import { useFeedbackStats } from '@/core/hooks/use-feedback';
import { useLocale } from '@/core/hooks/use-locale';
import { useLocations } from '@/core/hooks/use-locations';
import { useNavigateShell } from '@/core/hooks/use-navigate-shell';
import {
  useReputationOverview,
  useReputationSummary,
} from '@/core/hooks/use-reputation';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { shellPaths } from '@/core/router/app-paths';
import { useCurrentWorkspace } from '@/core/store/workspace.store';

const LOCATIONS_LIMIT = 200;
const ALL = '__all__';
/** Below this text-review response rate the KPI carries a "low" flag. */
const LOW_RESPONSE_RATE = 0.5;
const DASH = '–';

export function ReputationDashboardPage() {
  return (
    <AppIonPage>
      <AppPageContent>
        <ReputationLayout>
          <ReputationDashboardPageContent />
        </ReputationLayout>
      </AppPageContent>
    </AppIonPage>
  );
}

function ReputationDashboardPageContent() {
  const dict = useDictionary();
  const d = dict.reputation.dashboard;
  const scopeDict = dict.reputation.scope;
  const { navigateShell } = useNavigateShell();
  const workspace = useCurrentWorkspace();
  const workspaceId = workspace?.id ?? '';
  const locale = useLocale();

  const [scope, setScope] = useState<string>(ALL);
  const [rangeDays, setRangeDays] = useState<ReputationRangeDays>(30);
  const locationId = scope === ALL ? undefined : scope;
  const from = useMemo(() => reputationWindowFrom(rangeDays), [rangeDays]);
  useReputationScopeLocation(locationId);
  const { isSyncCompleteForScope } = useReputationSync();

  const { locations } = useLocations(workspaceId || undefined, {
    limit: LOCATIONS_LIMIT,
  });

  const { data: overview, isLoading } = useReputationOverview(
    workspaceId || undefined,
    { locationId, from },
  );
  const {
    data: summary,
    isLoading: summaryLoading,
    isError: summaryError,
  } = useReputationSummary(
    workspaceId || undefined,
    { locationId },
    !!overview && overview.totalReviews > 0,
  );
  const { isFeatureEnabled } = useEntitlements();
  const feedbackEnabled = isFeatureEnabled('feature.feedback');
  const { data: feedbackStats } = useFeedbackStats(
    feedbackEnabled ? workspaceId || undefined : undefined,
    { enabled: feedbackEnabled && !!workspaceId },
  );

  const hasNoLinkedLocations =
    !isLoading && overview && overview.linkedLocationCount === 0;

  const goInbox = (params?: Record<string, string>) => {
    const merged = { ...params };
    if (locationId && !merged.locationId) merged.locationId = locationId;
    const search =
      Object.keys(merged).length > 0
        ? `?${new URLSearchParams(merged).toString()}`
        : '';
    navigateShell(`${shellPaths.reputationInbox}${search}`);
  };

  const runInsightAction = (insight: ReputationInsightDto) => {
    const action = insight.action;
    if (!action) return;
    if (action.kind === 'inbox') {
      const params: Record<string, string> = {};
      if (action.aspect) params.aspect = action.aspect;
      if (action.sentiment) params.sentiment = action.sentiment;
      if (action.locationId) params.locationId = action.locationId;
      goInbox(params);
      return;
    }
    if (action.kind === 'location' && action.locationId) {
      navigateShell(shellPaths.moreWorkspaceLocationDetail(action.locationId));
      return;
    }
    if (action.kind === 'analytics') {
      navigateShell(shellPaths.reputationAnalytics);
    }
  };

  const locationPicker = (
    <LocationCombobox
      value={scope}
      onChange={setScope}
      options={locations.map((loc) => ({
        id: loc.id,
        name: loc.name ?? loc.id,
      }))}
      allValue={ALL}
      allLabel={scopeDict.allLocations}
      searchPlaceholder={scopeDict.searchPlaceholder}
      emptyText={scopeDict.noResults}
      className="w-auto max-w-[11rem] md:max-w-xs"
    />
  );

  const rangeControl = (fullWidth: boolean) => (
    <SegmentedControl
      ariaLabel={d.range.label}
      value={String(rangeDays) as `${ReputationRangeDays}`}
      onChange={(next) => setRangeDays(Number(next) as ReputationRangeDays)}
      options={REPUTATION_RANGE_DAYS.map((days) => ({
        value: String(days) as `${ReputationRangeDays}`,
        label: d.range[`d${days}`],
      }))}
      fullWidth={fullWidth}
    />
  );

  const headerActions = (
    <>
      {locationPicker}
      <div className="hidden md:block">{rangeControl(false)}</div>
      <Button
        className="hidden md:inline-flex"
        onClick={() => goInbox({ responded: 'false' })}
      >
        <Reply className="mr-2 size-4" aria-hidden />
        {d.replyCta}
      </Button>
    </>
  );

  const hasReviews = (overview?.totalReviews ?? 0) > 0;
  const showContent =
    !isLoading && overview && !hasNoLinkedLocations && isSyncCompleteForScope;

  return (
    <AppPageLayout>
      <PageHeader
        title={d.title}
        eyebrow={d.range[`d${rangeDays}`]}
        actions={headerActions}
        breadcrumb={
          <>
            {dict.nav.navDomainWork}
            <span className="mx-1.5 text-muted-foreground/70">/</span>
            {dict.nav.reputation}
          </>
        }
      />
      <ReputationPageBanners />

      <div className="flex min-w-0 flex-1 flex-col gap-4">
        <div className="md:hidden">{rangeControl(true)}</div>

        {isLoading && (
          <div className="grid grid-cols-2 gap-3 *:min-w-0 xl:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton
                key={i}
                className="h-24 w-full rounded-[var(--r-card)]"
              />
            ))}
          </div>
        )}

        {hasNoLinkedLocations && (
          <ListEmptyState
            icon={<Plug className="h-5 w-5" />}
            title={d.empty.title}
            description={d.empty.description}
            action={
              <Button
                size="sm"
                onClick={() =>
                  navigateShell(shellPaths.moreWorkspaceIntegrations)
                }
              >
                {d.empty.cta}
              </Button>
            }
          />
        )}

        {!hasNoLinkedLocations && !isSyncCompleteForScope && (
          <ReputationSyncPlaceholder kind="metrics" />
        )}

        {showContent && (
          <>
            <KpiStrip
              overview={overview}
              hasReviews={hasReviews}
              feedback={
                feedbackEnabled && feedbackStats
                  ? {
                      total: feedbackStats.total,
                      open: feedbackStats.unrepliedCount,
                    }
                  : null
              }
              onOpenFeedback={() => navigateShell(shellPaths.feedbackActivity)}
              onOpenUnanswered={() => goInbox({ responded: 'false' })}
            />

            {!hasReviews ? (
              <ListEmptyState
                icon={<Star className="h-5 w-5" />}
                title={d.noReviews.title}
                description={d.noReviews.description}
              />
            ) : null}

            {/* DOM order = desktop grid (summary | reviews, competitors | attention);
                mobile reorders: summary, attention, reviews, competitors. */}
            <div className="grid min-w-0 gap-3 xl:grid-cols-[minmax(0,1.55fr)_minmax(0,1fr)] [&>*]:min-w-0 [&>*>*]:h-full">
              {hasReviews ? (
                <div className="order-1 xl:order-none">
                  <AiSummaryCard
                    summary={summary}
                    loading={summaryLoading || summary?.status === 'generating'}
                    unavailable={
                      summaryError || summary?.status === 'unavailable'
                    }
                    labels={d.summary}
                    locale={locale}
                    onInbox={() => goInbox({ responded: 'false' })}
                    hideFooter
                  />
                </div>
              ) : null}
              {hasReviews ? (
                <div className="order-3 xl:order-none">
                  <OverviewReviewsCard
                    overview={overview}
                    labels={d.reviewsCard}
                    locale={locale}
                    onViewAll={() => goInbox()}
                  />
                </div>
              ) : null}
              <div className="order-4 xl:order-none">
                <OverviewCompetitorCard
                  teaser={overview.competitorTeaser}
                  labels={d.competitorCard}
                  locale={locale}
                  onOpen={() =>
                    navigateShell(
                      locationId
                        ? `${shellPaths.reputationCompetitors}?${new URLSearchParams({ locationId }).toString()}`
                        : shellPaths.reputationCompetitors,
                    )
                  }
                />
              </div>
              <div className="order-2 xl:order-none">
                <OverviewAttentionCard
                  insights={overview.insights}
                  templates={d.insights}
                  urgentCount={overview.unrepliedCriticalCount}
                  labels={d.attention}
                  onInsight={runInsightAction}
                  onOpenUrgent={() => goInbox({ responded: 'false' })}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </AppPageLayout>
  );
}

function KpiStrip({
  overview,
  hasReviews,
  feedback,
  onOpenFeedback,
  onOpenUnanswered,
}: {
  overview: NonNullable<ReturnType<typeof useReputationOverview>['data']>;
  /** False for a location with no reviews: values show a dash, not a fake 0. */
  hasReviews: boolean;
  feedback: { total: number; open: number } | null;
  onOpenFeedback: () => void;
  onOpenUnanswered: () => void;
}) {
  const dict = useDictionary();
  const d = dict.reputation.dashboard;
  const locale = useLocale();
  const k = d.kpi;
  const sf = d.metrics.scoreFormula as ScoreFormulaLabels & {
    lowConfidence: string;
    lowConfidenceDesc: string;
    scoreStrong: string;
    scoreFair: string;
    scoreWeak: string;
    scoreDesc: string;
  };

  const scoreFooter = reputationScoreFooter(overview.score, sf);
  const lowResponse =
    overview.textReviewCount > 0 &&
    overview.responseRateWithText < LOW_RESPONSE_RATE;
  const unanswered = Math.max(
    0,
    overview.totalReviews - overview.respondedCount,
  );
  const series = overview.kpiSeries;
  const cta = (text: string) => (
    <span className="font-semibold text-[color:var(--ghost-accent-fg)]">
      {text}
    </span>
  );

  return (
    <div className="grid grid-cols-2 gap-3 *:min-w-0 xl:grid-cols-4">
      <KpiCard
        label={d.metrics.reputationScore}
        badge={
          <ReputationScoreFormulaPopover score={overview.score} labels={sf} />
        }
        value={hasReviews ? formatReputationScoreValue(overview.score) : DASH}
        valueSuffix={hasReviews ? '/ 100' : undefined}
        change={{
          label: scoreFooter.trend,
          direction:
            scoreFooter.direction === 'neutral'
              ? 'flat'
              : scoreFooter.direction,
        }}
        hideSparkOnMobile
        spark={sparkSeries(series.map((point) => point.score))}
      />
      <KpiCard
        label={k.averageRating}
        value={
          hasReviews
            ? overview.averageRating.toLocaleString(locale, {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1,
              })
            : DASH
        }
        valueSuffix={
          hasReviews ? (
            <span className="text-[color:var(--chart-4)]" aria-hidden>
              ★★★★★
            </span>
          ) : undefined
        }
        sub={k.reviewsCount.replace(
          '{count}',
          overview.totalReviews.toLocaleString(locale),
        )}
        hideSparkOnMobile
        spark={sparkSeries(series.map((point) => point.averageRating))}
        sparkTone="rating"
      />
      <KpiCard
        label={d.metrics.responseRate}
        badge={
          lowResponse ? (
            <Badge variant="warning">{k.responseLow}</Badge>
          ) : undefined
        }
        value={
          hasReviews && overview.textReviewCount > 0
            ? `${Math.round(overview.responseRateWithText * 100)} %`
            : DASH
        }
        sub={k.responseDetail
          .replace('{responded}', String(overview.respondedTextCount))
          .replace('{total}', String(overview.textReviewCount))}
        hideSparkOnMobile
        spark={sparkSeries(series.map((point) => point.responseRateWithText))}
        sparkTone="warning"
      />
      {feedback ? (
        <KpiCard
          label={k.privateFeedback}
          value={String(feedback.total)}
          valueSuffix={k.feedbackOpen.replace('{open}', String(feedback.open))}
          sub={cta(k.feedbackCta)}
          onClick={onOpenFeedback}
        />
      ) : (
        <KpiCard
          label={k.unanswered}
          value={unanswered.toLocaleString(locale)}
          valueSuffix={k.unansweredCritical.replace(
            '{count}',
            String(overview.unrepliedCriticalCount),
          )}
          sub={cta(k.unansweredCta)}
          onClick={onOpenUnanswered}
        />
      )}
    </div>
  );
}

/** Sparkline input: thinned, and omitted when there is no real history. */
function sparkSeries(values: number[]): number[] | undefined {
  return values.length > 1 ? downsample(values) : undefined;
}
